import { useNavigate, useParams } from "react-router-dom";
import { Button, Icon, SidebarItem, Spinner } from "../ui";
import { RUNNING_STATUSES, useProject } from "../hooks/useProject";
import { Step1Descriptors } from "./Step1Descriptors";
import { Step2Sources } from "./Step2Sources";
import { Step3References } from "./Step3References";
import { Step4Extraction } from "./Step4Extraction";
import { Step5OverviewEN } from "./Step5OverviewEN";
import { Step6ReviewEN } from "./Step6ReviewEN";
import { Step7Translation } from "./Step7Translation";
import { Step8Download } from "./Step8Download";

const STEP_LABELS = [
  "Criada",
  "Descritores",
  "Fontes",
  "Referências",
  "Extração",
  "Visão EN",
  "Revisão EN",
  "Tradução PT",
  "Entregue",
];

interface ShellStepDef {
  num: number;
  title: string;
  sub: string;
}

const STEPS: ShellStepDef[] = [
  { num: 1, title: "Descritores", sub: "Busca regulatória" },
  { num: 2, title: "Fontes", sub: "Seleção e ranking" },
  { num: 3, title: "Referências", sub: "ABNT NBR 6023" },
  { num: 4, title: "Extração", sub: "Pipeline LLM" },
  { num: 5, title: "Visão geral", sub: "Monografia EN" },
  { num: 6, title: "Revisão", sub: "Inglês final" },
  { num: 7, title: "Tradução", sub: "PT-BR" },
  { num: 8, title: "Entrega", sub: "Download DOCX" },
];

function boundedStep(value: string | undefined, fallback: number): number {
  const parsed = value ? Number.parseInt(value, 10) : fallback;
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, 1), 8);
}

export function ProjectShell() {
  const { id, n } = useParams<{ id: string; n?: string }>();
  const navigate = useNavigate();
  const { data: project, isPending, isError } = useProject(id);

  const onBack = () => navigate("/projects");

  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center caption gap-3">
        <Spinner /> Carregando monografia...
      </div>
    );
  }
  if (isError || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div
          role="alert"
          className="caption px-4 py-6 border border-[var(--strike)] text-[var(--strike)] max-w-md text-center"
        >
          Não foi possível carregar esta monografia. Volte para a lista e tente novamente.
        </div>
      </div>
    );
  }

  const statusUnlock =
    project.status === "reviewing_en" ? 6 :
    project.status === "translating" || project.status === "reviewing_pt" ? 7 :
    project.status === "building" || project.status === "done" ? 8 :
    project.current_step;
  const allowedMaxStep = Math.min(8, Math.max(project.current_step, statusUnlock));
  const requestedStep = boundedStep(n, allowedMaxStep);
  const currentStep = Math.min(requestedStep, allowedMaxStep);
  const stepLabel = STEP_LABELS[currentStep] ?? `Passo ${currentStep}`;
  const prevStep = STEPS[currentStep - 2];
  const nextStep = STEPS[currentStep];
  const isRunning = RUNNING_STATUSES.has(project.status);
  const statusLabel = isRunning ? `${project.status} · polling 2s` : project.status;

  const goStep = (step: number) => {
    const optimisticMaxStep = Math.min(8, Math.max(allowedMaxStep, currentStep + 1));
    if (step <= optimisticMaxStep) {
      navigate(`/projects/${project.id}/step/${step}`);
    }
  };

  const mock = false;
  const stepProps = { project, mock, onAdvance: goStep };
  const children =
    currentStep === 1 ? <Step1Descriptors {...stepProps} /> :
    currentStep === 2 ? <Step2Sources project={project} onAdvance={goStep} /> :
    currentStep === 3 ? <Step3References project={project} onAdvance={goStep} /> :
    currentStep === 4 ? <Step4Extraction {...stepProps} /> :
    currentStep === 5 ? <Step5OverviewEN project={project} mock={mock} onAdvance={goStep} /> :
    currentStep === 6 ? <Step6ReviewEN project={project} mock={mock} onAdvance={goStep} /> :
    currentStep === 7 ? <Step7Translation project={project} mock={mock} onAdvance={goStep} /> :
    currentStep === 8 ? <Step8Download project={project} /> :
    null;

  return (
    <div className="min-h-screen anim-fade">
      <header className="border-b-2 border-[var(--ink)] sticky top-0 bg-[var(--paper)] z-30">
        <div className="max-w-[1600px] mx-auto px-8 py-3.5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-5 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="caption hover:text-[var(--ink)] flex items-center gap-1.5 shrink-0"
            >
              <Icon.ArrowLeft size={12} /> Monografias
            </button>
            <span className="w-px h-5 bg-[var(--rule)] shrink-0" />
            <div className="min-w-0 flex items-baseline gap-3">
              <span className="caption shrink-0">
                Nº {project.id.replace("p_", "").toUpperCase()}
              </span>
              <span className="display-5 truncate">{project.drug_api_name}</span>
              <span className="caption hidden xl:inline">
                IUPAC · {project.iupac_name ?? "—"}
              </span>
              <span className="caption hidden lg:inline">
                CAS · {project.cas_number ?? "—"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="mono text-[12px] tabular text-[var(--ink-3)] hidden md:inline">
              [{currentStep}/8] {stepLabel}
            </span>
            <span
              aria-live="polite"
              className="mono text-[12px] tabular text-[var(--ink-3)] hidden lg:inline"
            >
              {statusLabel}
            </span>
            <Button variant="line" size="sm" icon={Icon.Settings}>
              Projeto
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-0">
        <aside className="col-span-12 lg:col-span-3 px-8 py-10 border-r border-[var(--rule)]">
          <div className="sticky top-[80px]">
            <div className="eyebrow mb-4">§ Sumário</div>
            <div className="hr mb-2" />
            <nav>
              {STEPS.map((s) => {
                const status =
                  s.num < currentStep ? "done" : s.num === currentStep ? "current" : "future";
                const disabled = s.num > allowedMaxStep;
                const tooltip = disabled ? `Disponível após Etapa ${s.num - 1}` : undefined;
                return (
                  <div
                    key={s.num}
                    aria-disabled={disabled || undefined}
                    title={tooltip}
                  >
                    <SidebarItem
                      num={s.num}
                      title={s.title}
                      sub={s.sub}
                      status={status}
                      active={s.num === currentStep}
                      disabled={disabled}
                      onClick={disabled ? undefined : () => goStep(s.num)}
                    />
                  </div>
                );
              })}
            </nav>

            <div className="mt-10 space-y-3">
              <div className="eyebrow">Identificação</div>
              <div className="hr" />
              <div className="space-y-2">
                <div>
                  <div className="caption">IUPAC</div>
                  <div className="text-[12px] mono leading-snug text-[var(--ink-2)] mt-0.5 break-all">
                    {project.iupac_name ?? "—"}
                  </div>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="caption">CAS</span>
                  <span className="mono text-[12px] text-[var(--ink)]">
                    {project.cas_number ?? "—"}
                  </span>
                </div>
                {project.molecular_formula && (
                  <div className="flex justify-between gap-2">
                    <span className="caption">Fórmula</span>
                    <span className="mono text-[12px] text-[var(--ink)]">
                      {project.molecular_formula}
                    </span>
                  </div>
                )}
                {project.molecular_weight && (
                  <div className="flex justify-between gap-2">
                    <span className="caption">PM</span>
                    <span className="mono text-[12px] text-[var(--ink)] tabular">
                      {project.molecular_weight}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-9 px-10 lg:px-14 py-12 min-h-[calc(100vh-60px)]">
          {children}

          <div className="mt-20 pt-8 border-t-2 border-[var(--ink)] flex items-center justify-between gap-4">
            {currentStep > 1 && prevStep ? (
              <Button
                variant="ghost"
                icon={Icon.ArrowLeft}
                onClick={() => goStep(currentStep - 1)}
              >
                Etapa {currentStep - 1} · {prevStep.title}
              </Button>
            ) : (
              <span />
            )}
            <span className="caption">Página {currentStep} de 8</span>
            {currentStep < 8 && nextStep && currentStep + 1 <= allowedMaxStep ? (
              <Button
                variant="ink"
                iconRight={Icon.ArrowRight}
                onClick={() => goStep(currentStep + 1)}
              >
                Etapa {currentStep + 1} · {nextStep.title}
              </Button>
            ) : (
              <span />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
